import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as GET_GROUP_NEGOTIATIONS_QUERY_KEY } from './useGetGroupNegotiations';

export type ApiRequest_Reopen_Group_Negotiation = {
  communityId: number;
  negotiationId: number;
};

const METHOD = 'POST';
const ENDPOINT = 'communities/{communityId}/group-negotiations/{negotiationId}/reopen';

export const useReopenGroupNegotiation = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Reopen_Group_Negotiation) => {
      try {
        const data = await handleApiCall<unknown, Record<string, never>>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            communityId: params.communityId.toString(),
            negotiationId: params.negotiationId.toString(),
          }),
          {}
        );

        enqueueSnackbar('Group re-opened successfully!', { variant: 'success' });
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to re-open group.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: GET_GROUP_NEGOTIATIONS_QUERY_KEY({ communityId: variables.communityId }),
      });
      queryClient.invalidateQueries({
        queryKey: ['community', 'group-negotiation', variables.communityId, variables.negotiationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['community', 'activities', variables.communityId],
      });
    },
  });
};